import { doc, getDoc, writeBatch } from 'firebase/firestore';
import type { AppSettingsDoc, SystemSettingsDoc } from '@flintmint/core-shared/firestore/schemas';
import {
  APP_SETTINGS_RANGES,
  isValidSettingValue,
  resolveAppSettings,
  resolveSystemSettings,
  SYSTEM_SETTINGS_RANGES,
} from '@flintmint/core-shared/utils';
import { getFirestore } from './firebase-service';

export type BillingSettings = Pick<
  SystemSettingsDoc,
  'aiPreviewCredits' | 'trialDays' | 'trialCredits' | 'gracePeriodQuotaPercent'
> &
  Pick<AppSettingsDoc, 'quotaPercentUsedWarning' | 'creditsPerTypicalNote'>;

/**
 * The admin-editable billing settings, each missing or invalid value (or a missing doc) resolved to its default.
 * `unsavedKeys` lists the keys resolved that way, so saving the shown default repairs the stored value.
 */
export const fetchBillingSettings = async (): Promise<{
  settings: BillingSettings;
  unsavedKeys: (keyof BillingSettings)[];
}> => {
  const firestore = getFirestore();
  const [systemSnapshot, appSnapshot] = await Promise.all([
    getDoc(doc(firestore, 'settings', 'system')),
    getDoc(doc(firestore, 'settings', 'app')),
  ]);
  const stored = { system: systemSnapshot.data(), app: appSnapshot.data() };
  const capture = (message: string) => console.warn(message);
  const settings = { ...resolveSystemSettings(stored.system, capture), ...resolveAppSettings(stored.app, capture) };
  const ranges = { ...SYSTEM_SETTINGS_RANGES, ...APP_SETTINGS_RANGES };
  const unsavedKeys = (Object.keys(SETTINGS_DOC_ID) as (keyof BillingSettings)[]).filter(
    (key) => !isValidSettingValue(stored[SETTINGS_DOC_ID[key]]?.[key], ranges[key])
  );
  return { settings, unsavedKeys };
};

/** The `settings/system` grace percent, or its default when the doc or a valid value is missing. */
export const fetchGracePeriodQuotaPercent = async (): Promise<number> => {
  const snapshot = await getDoc(doc(getFirestore(), 'settings', 'system'));
  // Only the grace percent is read here, so warnings about the other keys are not this reader's to report.
  return resolveSystemSettings(snapshot.data(), () => {}).gracePeriodQuotaPercent;
};

/** Merges each changed value into its settings doc in one atomic batch, creating a missing doc and leaving every other field as is. */
export const saveBillingSettings = async (changes: Partial<BillingSettings>): Promise<void> => {
  const firestore = getFirestore();
  const updatedAt = new Date().toISOString();
  const batch = writeBatch(firestore);
  for (const key of Object.keys(changes) as (keyof BillingSettings)[]) {
    batch.set(doc(firestore, 'settings', SETTINGS_DOC_ID[key]), { [key]: changes[key], updatedAt }, { merge: true });
  }
  await batch.commit();
};

const SETTINGS_DOC_ID: Record<keyof BillingSettings, 'system' | 'app'> = {
  aiPreviewCredits: 'system',
  trialDays: 'system',
  trialCredits: 'system',
  gracePeriodQuotaPercent: 'system',
  quotaPercentUsedWarning: 'app',
  creditsPerTypicalNote: 'app',
};
